import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter-172 — AI Creator self-signup application form. Approval-gated.
 */
const CreatorApply = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    creator_name: '', studio_name: '', email: '', password: '',
    bio: '', portfolio_url: '', showreel_url: '',
    specialties: '', industries: '', languages: '', licensing_terms: '',
  });

  const onSubmit = async () => {
    if (!form.creator_name || !form.email || !form.password) {
      toast.error('Creator name, email, and password are required');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const csv = (s) => s ? s.split(',').map(x => x.trim()).filter(Boolean) : [];
      await api.post('/ai-creators/register', {
        email: form.email.toLowerCase(),
        password: form.password,
        creator_name: form.creator_name,
        studio_name: form.studio_name || undefined,
        bio: form.bio || undefined,
        portfolio_url: form.portfolio_url || undefined,
        showreel_url: form.showreel_url || undefined,
        specialties: csv(form.specialties),
        industries: csv(form.industries),
        languages: csv(form.languages),
        licensing_terms: form.licensing_terms || undefined,
      });
      toast.success('Application submitted — log in to view status.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Application failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-950 via-zinc-950 to-fuchsia-950 px-4 py-10" data-testid="creator-apply-page">
      <Card className="w-full max-w-2xl bg-zinc-900/80 border-zinc-800 backdrop-blur">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center mb-2">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Apply as an AI Avatar Creator</CardTitle>
          <CardDescription className="text-zinc-400">
            Free to join · Get paid when your avatars get booked. Approval gated to keep marketplace quality high.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Creator name *</Label>
              <Input value={form.creator_name} onChange={e => setForm(f => ({...f, creator_name: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white" data-testid="creator-name-input" />
            </div>
            <div>
              <Label className="text-zinc-300">Studio name</Label>
              <Input value={form.studio_name} onChange={e => setForm(f => ({...f, studio_name: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300">Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white" data-testid="creator-email-input" />
            </div>
            <div>
              <Label className="text-zinc-300">Password *</Label>
              <Input type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white" data-testid="creator-password-input" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-300">Bio</Label>
            <Textarea rows={3} value={form.bio} onChange={e => setForm(f => ({...f, bio: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white" placeholder="Briefly describe your studio/practice and the kind of AI avatars you create." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Portfolio URL</Label>
              <Input value={form.portfolio_url} onChange={e => setForm(f => ({...f, portfolio_url: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white" placeholder="https://..." />
            </div>
            <div>
              <Label className="text-zinc-300">Showreel URL</Label>
              <Input value={form.showreel_url} onChange={e => setForm(f => ({...f, showreel_url: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white" placeholder="https://..." />
            </div>
          </div>
          <div>
            <Label className="text-zinc-300">Specialties (comma-separated)</Label>
            <Input value={form.specialties} onChange={e => setForm(f => ({...f, specialties: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white" placeholder="hyper-realistic, animated, multilingual" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Industries</Label>
              <Input value={form.industries} onChange={e => setForm(f => ({...f, industries: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white" placeholder="beauty, tech" />
            </div>
            <div>
              <Label className="text-zinc-300">Languages</Label>
              <Input value={form.languages} onChange={e => setForm(f => ({...f, languages: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white" placeholder="en, es, fr" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-300">Licensing terms (optional)</Label>
            <Textarea rows={2} value={form.licensing_terms} onChange={e => setForm(f => ({...f, licensing_terms: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
          <Button
            onClick={onSubmit}
            disabled={loading}
            className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:opacity-90 text-white"
            data-testid="creator-apply-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Submit application
          </Button>
          <p className="text-center text-xs text-zinc-400 mt-3">
            Already have a creator account? <Link to="/login" className="text-fuchsia-400 hover:underline">Sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreatorApply;
